import React, { useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Drawer,
  Fab,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Announcement as AnnouncementIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  Home as HomeIcon,
  Inventory,
  Assignment,
  ContactPhone as ContactPhoneIcon,
  SupervisorAccount as MinistrosIcon,
  VideoLibrary as VideoLibraryIcon,
  EventAvailable as ReservasIcon,
} from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";

export function InternalNavigation({ mobileOpen, setMobileOpen }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Define as opções de menu baseado no role do usuário
  const getMenuItems = () => {
    const commonItems = [
      {
        text: "Dashboard",
        path: "/dashboard",
        icon: <DashboardIcon />,
        roles: ["admin", "secretary"],
      },
      {
        text: "Avisos",
        path: "/announcements",
        icon: <AnnouncementIcon />,
        roles: ["admin", "secretary"],
      },
      {
        text: "Mensagens Padrão",
        path: "/mensagens",
        icon: <AnnouncementIcon />,
        roles: ["admin", "secretary"],
      },
      {
        text: "Contactações",
        path: "/contactacoes-escala", 
        icon: <ContactPhoneIcon />,
        roles: ["admin", "secretary"],
      },
      {
        text: "Ministros",
        path: "/ministros",
        icon: <MinistrosIcon />,
        roles: ["admin", "secretary"],
      },
      {
        text: "Reservas",
        path: "/reservas",
        icon: <ReservasIcon />,
        roles: ["admin", "secretary"],
      },
    ];

    const adminItems = [
      {
        text: "Usuários",
        path: "/admin/users",
        icon: <PeopleIcon />,
        roles: ["admin"],
      },
      {
        text: "Produtos",
        path: "/admin/produtos",
        icon: <Inventory />,
        roles: ["admin"],
      },
      {
        text: "Inscrições",
        path: "/admin/inscricoes",
        icon: <Assignment />,
        roles: ["admin"],
      },
      {
        text: "Ministrações",
        path: "/admin/ministracoes",
        icon: <VideoLibraryIcon />,
        roles: ["admin"],
      },
    ];

    return [...commonItems, ...adminItems].filter((item) =>
      item.roles.includes(user?.role),
    );
  };

  const menuItems = getMenuItems();

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isActivePage = (path) => {
    return location.pathname === path;
  };

  const MenuContent = () => (
    <Box sx={{ width: { xs: 280, md: 240 }, height: "100%" }}>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "primary.main",
          color: "background.default",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Portal ZS
        </Typography>
        <Typography variant="caption" sx={{ color: "background.default" }}>
          {user?.role === "admin" ? "Administrador" : "Secretária"}
        </Typography>
      </Box>

      <List sx={{ pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActivePage(item.path)}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "background.default",
                  "&:hover": {
                    backgroundColor: "secondary.main",
                  },
                  "& .MuiListItemIcon-root": {
                    color: "background.default",
                  },
                },
                "&:hover": {
                  backgroundColor: "background.default",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: isActivePage(item.path) ? "inherit" : "action.active",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: isActivePage(item.path) ? "bold" : "normal",
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}

        <Divider sx={{ my: 2, mx: 2 }} />

        <ListItem disablePadding sx={{ px: 1 }}>
          <ListItemButton
            onClick={() => handleNavigation("/")}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              "&:hover": {
                backgroundColor: "background.default",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText
              primary="Voltar ao Site"
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  // Mobile: Drawer + FAB
  if (isMobile) {
    return (
      <>
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: 280,
              backgroundColor: "background.default",
            },
          }}
        >
          <MenuContent />
        </Drawer>

        {!mobileOpen && (
          <Fab
            color="primary"
            size="medium"
            onClick={() => setMobileOpen(true)}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 1000,
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "secondary.main",
              },
            }}
          >
            <MenuIcon sx={{ color: "background.default" }} />
          </Fab>
        )}
      </>
    );
  }

  // Desktop: Sidebar fixo
  return (
    <Paper
      sx={{
        borderRight: "2px solid primary.main",
      }}
    >
      <MenuContent />
    </Paper>
  );
}
